// Smart links in rich text — Jira's inlineCard, drawn as Jira draws it.
//
// Paste a work item's address (https://<this site>/HR2023-527) into a
// rich-text field and it becomes a card: the work type's icon, the key, the
// summary and the status lozenge. An address on the client's OLD Jira site
// (https://<old>.atlassian.net/browse/KEY, client config) pastes as a card to
// this site's address of the key. Cards the clone carried over from Jira —
// and bare links to Jira work items — draw the same way. The node is ADF's own inlineCard ({ url }), so the
// stored document is what Jira would store and round-trips unchanged
// (utils/adfConverter.js).
//
// Reading: a click opens the work item as a tab; middle / Ctrl-click
// opens it behind (the href is the item's own /KEY path, which the tab
// shell's global handlers pick up). Editing: a click selects the card, and
// "Show as URL" turns it back into a plain link. A card whose item the
// reader cannot see — or whose address is not a work item — draws as its
// plain address, never as a broken card (smartLink.js, useIssueCard.js).

import { Node, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import api from '@app/services/api';
import { useOptionalTab } from '@platform/app-shell/frontend/contexts/TabContext';
import { handleIssueLinkActivation } from '@modules/zebra-search/frontend/utils/issueLinks';
import { workItemPath } from '@modules/database/work-items/frontend/utils/workItemLink';
import { cardKeyFromUrl, pastedWorkItemUrl, statusClass } from '@modules/database/work-items/frontend/utils/smartLink';
import { useIssueCard } from './useIssueCard';
import { loadSmartLinkConfig, legacyJiraHosts } from './smartLinkConfig';
import './SmartLink.css';

function SmartLinkView({ node, editor, selected, getPos }) {
  const url = node.attrs.url || '';
  const key = cardKeyFromUrl(url, window.location.origin);
  const card = useIssueCard(key);
  const tabs = useOptionalTab();
  const editing = editor.isEditable;

  const showAsUrl = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getPos();
    if (typeof pos !== 'number') return;
    editor.chain().focus()
      .insertContentAt({ from: pos, to: pos + node.nodeSize }, { type: 'text', text: url, marks: [{ type: 'link', attrs: { href: url } }] })
      .run();
  };

  const onClick = (e) => {
    // Editing: a plain click selects the card (ProseMirror does that on
    // mousedown) and must not navigate away from an unsaved edit.
    if (editing) {
      if (e.button === 0 && !(e.ctrlKey || e.metaKey || e.shiftKey || e.altKey)) e.preventDefault();
      return;
    }
    // Reading: the field around the text opens its editor on a click —
    // a click on the card is the card's.
    e.stopPropagation();
    if (!card) return; // a plain address follows its own href (new tab)
    handleIssueLinkActivation(e, (k) => {
      if (tabs?.openTab) tabs.openTab(workItemPath(k), k);
      else window.location.assign(workItemPath(k));
    }, card.key);
  };

  let body;
  if (key && card) {
    const sc = statusClass(card.status?.colorName);
    body = (
      <a className="rte-smart-link" href={workItemPath(card.key)} onClick={onClick} title={`${card.key}: ${card.summary}`} draggable={false}>
        {card.issuetype?.iconUrl && (
          <img className="rte-smart-link-icon" src={api.getJiraImageUrl(card.issuetype.iconUrl)} alt={card.issuetype.name || ''} width="16" height="16" />
        )}
        <span className="rte-smart-link-text">{card.key}: {card.summary}</span>
        {card.status?.name && <span className={`rte-smart-link-status status-${sc}`}>{card.status.name}</span>}
      </a>
    );
  } else if (key && card === undefined) {
    // Loading: the key alone, so the line does not jump when the card lands.
    body = (
      <a className="rte-smart-link rte-smart-link--loading" href={workItemPath(key)} onClick={onClick} draggable={false}>
        <span className="rte-smart-link-text">{key}</span>
      </a>
    );
  } else {
    body = (
      <a className="rte-smart-link-plain" href={url} target="_blank" rel="noopener noreferrer" onClick={onClick} draggable={false}>{url}</a>
    );
  }

  return (
    <NodeViewWrapper as="span" className={`rte-smart-link-wrap${selected && editing ? ' is-selected' : ''}`}>
      {body}
      {selected && editing && (
        <span className="rte-smart-link-menu" contentEditable={false}>
          <button type="button" onMouseDown={showAsUrl}>Show as URL</button>
        </span>
      )}
    </NodeViewWrapper>
  );
}

export const SmartLink = Node.create({
  name: 'inlineCard',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,
  // Ahead of Link: its parse rule (a[href]) and its paste handling would
  // otherwise take a card for an ordinary link.
  priority: 1001,

  addAttributes() {
    return { url: { default: null } };
  },

  parseHTML() {
    return [{ tag: 'a[data-inline-card]', getAttrs: (el) => ({ url: el.getAttribute('href') }) }];
  },

  renderHTML({ node }) {
    return ['a', { 'data-inline-card': '', href: node.attrs.url }, node.attrs.url || ''];
  },

  renderText({ node }) {
    return node.attrs.url || '';
  },

  addNodeView() {
    return ReactNodeViewRenderer(SmartLinkView);
  },

  // The old Jira host(s) must be known before the first paste.
  onCreate() {
    loadSmartLinkConfig();
  },

  addProseMirrorPlugins() {
    const type = this.type;
    return [
      new Plugin({
        key: new PluginKey('smartLinkPaste'),
        props: {
          // A bare address of one of this site's work items — or of the old
          // Jira site's, rewritten to this site's — becomes a card. Pasted
          // over a selection it stays Link's (the selection becomes the link
          // text), and inside code it stays text.
          handlePaste(view, event) {
            if (!view.editable) return false;
            const url = pastedWorkItemUrl(event.clipboardData?.getData('text/plain'), window.location.origin, legacyJiraHosts());
            if (!url) return false;
            const { state } = view;
            if (!state.selection.empty || state.selection.$from.parent.type.spec.code) return false;
            const tr = state.tr.replaceSelectionWith(type.create({ url }), false).insertText(' ');
            view.dispatch(tr.scrollIntoView());
            return true;
          },
        },
      }),
    ];
  },
});

export default SmartLink;
